// Copyright 2026. WebPros International GmbH. All rights reserved.

import { FC } from 'react';
import {
    IVmResponse,
    VirtualServerSettingsRequest,
    VZNetfilterStatus,
} from 'common/api/resources/ComputeResourceVm';
import {
    GridCol,
    Item,
    Paragraph,
    Translate,
} from '@plesk/ui-library';
import {
    Column,
    Grid,
    ItemText,
} from 'common/components/ServerTabs/SettingsTab/Styles';
import VZNetfilterStatusControl from 'common/components/ServerTabs/components/Controls/VZNetfilterStatusControl';
import VZTUNTAPControl from 'common/components/ServerTabs/components/Controls/VZTUNTAPControl';
import VZPPPControl from 'common/components/ServerTabs/components/Controls/VZPPPControl';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import SuspendResumeControl from 'common/components/ServerTabs/components/Controls/SuspendResumeControl';

export interface IVZSettingsTabProps {
    server: IVmResponse;
    isUpdating: boolean;
    updateSettings: (settings: VirtualServerSettingsRequest) => void;
    isAdminSettings: boolean;
    isSuspendToggling: boolean;
    toggleSuspendStatus: (isSuspended: boolean) => void;
    canManageServers: boolean;
}

export const VZSettingsTab: FC<IVZSettingsTabProps> = ({
    server,
    isUpdating,
    updateSettings,
    isAdminSettings,
    toggleSuspendStatus,
    isSuspendToggling,
    canManageServers,
}) => {
    const enableVncConsole = () => {
        updateSettings({ vnc_enabled: true });
    };

    const netfilterChange = (value: VZNetfilterStatus) => {
        updateSettings({ netfilter: value });
    };

    const tunTAPChange = (value: boolean) => {
        updateSettings({ tun_tap: value });
    };

    const pppChange = (value: boolean) => {
        updateSettings({ ppp: value });
    };

    const isEditable = (key: string) => canManageServers || (server.settings[key]?.is_editable ?? false);

    return (
        <Grid gap="md" xs={1} sm={1} md={2} lg={2} xl={2}>
            <GridCol>
                <Column>
                    <Item
                        title={
                            <h3>
                                <SuspendResumeControl
                                    value={server.is_suspended}
                                    disabled={isSuspendToggling || !canManageServers}
                                    loading={isSuspendToggling}
                                    onChange={toggleSuspendStatus}
                                />
                            </h3>
                        }
                        view="card"
                    >
                        <Paragraph>
                            <Translate content="servers.tabs.settings.suspendResume.text" />
                        </Paragraph>
                    </Item>

                    <Item
                        title={
                            <h3 id="vz-netfilter-status-label">Netfilter</h3>
                        }
                        view="card"
                    >
                        <VZNetfilterStatusControl
                            inputId="vz-netfilter-status-input"
                            aria-labelledby="vz-netfilter-status-label"
                            value={server.settings.netfilter?.value ?? VZNetfilterStatus.STATELESS}
                            disabled={isUpdating || !isEditable('netfilter')}
                            onChange={netfilterChange}
                        />
                        <ItemText>
                            <Translate content="servers.tabs.settings.netfilter.text" />
                        </ItemText>
                    </Item>
                </Column>
            </GridCol>

            <GridCol>
                <Column>
                    {isAdminSettings && (
                        <Item
                            title={
                                <h3><Translate content="servers.tabs.settings.vncConsole.title" /></h3>
                            }
                            view="card"
                        >
                            <Paragraph>
                                <Translate content="servers.tabs.settings.vncConsole.description" />
                            </Paragraph>
                            <ButtonWithConfirmation
                                isLoading={isUpdating}
                                disabled={server.settings.vnc_enabled || !canManageServers}
                                buttonSize={SIZE.LG}
                                buttonIntent={INTENT_TYPE.PRIMARY}
                                confirmationButtonGhost={false}
                                translations={{
                                    title: <Translate content="servers.tabs.settings.vncConsole.confirmationDialog.title" />,
                                    text: <Translate content="servers.tabs.settings.vncConsole.confirmationDialog.content" />,
                                    button: <Translate content="servers.tabs.settings.vncConsole.confirmationDialog.button" />,
                                }}
                                confirmationButtonText={<Translate content='servers.tabs.settings.vncConsole.button' />}
                                handleConfirm={enableVncConsole}
                            />
                        </Item>
                    )}

                    <Item
                        title={
                            <h3>
                                <VZPPPControl
                                    label="PPP"
                                    value={server.settings.ppp?.value ?? false}
                                    disabled={isUpdating || !isEditable('ppp')}
                                    onChange={pppChange}
                                />
                            </h3>
                        }
                        view="card"
                    >
                        <Paragraph>
                            <Translate content="servers.tabs.settings.ppp.text" />
                        </Paragraph>
                    </Item>

                    <Item
                        title={
                            <h3>
                                <VZTUNTAPControl
                                    label="TUN/TAP"
                                    value={server.settings.tun_tap?.value ?? false}
                                    disabled={isUpdating || !isEditable('tun_tap')}
                                    onChange={tunTAPChange}
                                />
                            </h3>
                        }
                        view="card"
                    >
                        <Paragraph>
                            <Translate content="servers.tabs.settings.tunTAP.text" />
                        </Paragraph>
                    </Item>
                </Column>
            </GridCol>
        </Grid>
    );
};

export default VZSettingsTab;
